if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        this.mql = window.matchMedia('(min-width: 750px)');

        // Show the active variant image/GIF and the product image.
        this.showOnlyActiveMedia();

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener(
          'slideChanged',
          debounce(this.onSlideChanged.bind(this), 500)
        );

        this.elements.thumbnails
          .querySelectorAll('[data-target]')
          .forEach((mediaToSwitch) => {
            const button = mediaToSwitch.querySelector('button');

            if (button) {
              button.addEventListener(
                'click',
                this.setActiveMedia.bind(
                  this,
                  mediaToSwitch.dataset.target,
                  false
                )
              );
            }
          });

        if (
          this.dataset.desktopLayout &&
          this.dataset.desktopLayout.includes('thumbnail') &&
          this.mql.matches
        ) {
          this.removeListSemantic();
        }
      }

      showOnlyActiveMedia() {
        if (!this.elements.viewer) return;

        const mediaItems =
          this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]');

        let activeMedia =
          this.elements.viewer.querySelector('.product__media-item[data-media-id].is-active');

        if (!activeMedia && mediaItems.length > 0) {
          activeMedia = mediaItems[0];
          activeMedia.classList.add('is-active');
        }

        const productMedia = Array.from(mediaItems).find(
          (element) =>
            !element.classList.contains('product__media-item--variant')
        );

        mediaItems.forEach((element) => {
          const isActive = element === activeMedia;
          const isProductMedia = element === productMedia;
          const shouldShow = isActive || isProductMedia;

          element.hidden = !shouldShow;

          if (shouldShow) {
            element.style.removeProperty('display');
          } else {
            element.style.setProperty(
              'display',
              'none',
              'important'
            );
          }
        });
      }

      onSlideChanged(event) {
        if (!this.elements.thumbnails) return;

        const thumbnail =
          this.elements.thumbnails.querySelector(
            `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
          );

        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        if (!this.elements.viewer) return;

        const activeMedia =
          this.elements.viewer.querySelector(
            `.product__media-item[data-media-id="${mediaId}"]`
          );

        if (!activeMedia) {
          console.warn(`Media not found: ${mediaId}`);
          return;
        }

        // Show the selected GIF/image plus the primary product image only.
        const mediaItems =
          this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]');
        const productMedia = Array.from(mediaItems).find(
          (element) =>
            !element.classList.contains('product__media-item--variant')
        );

        mediaItems.forEach((element) => {
          const isActive = element === activeMedia;
          const isProductMedia = element === productMedia;
          const shouldShow = isActive || isProductMedia;

          element.classList.toggle('is-active', isActive);
          element.hidden = !shouldShow;

          if (shouldShow) {
            element.style.removeProperty('display');
          } else {
            element.style.setProperty(
              'display',
              'none',
              'important'
            );
          }
        });

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail =
              this.elements.thumbnails.querySelector(
                `[data-target="${mediaId}"]`
              );

            if (activeThumbnail) {
              activeThumbnail.parentElement.prepend(
                activeThumbnail
              );
            }
          }

          if (this.elements.viewer.slider) {
            this.elements.viewer.resetPages();
          }
        }

        this.preventStickyHeader();

        window.setTimeout(() => {
          if (this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({
              left: activeMedia.offsetLeft,
              behavior: 'smooth',
            });
          }

          if (
            !this.elements.thumbnails ||
            this.dataset.desktopLayout === 'stacked'
          ) {
            activeMedia.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
            });
          }
        });

        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;

        const activeThumbnail =
          this.elements.thumbnails.querySelector(
            `[data-target="${mediaId}"]`
          );

        this.setActiveThumbnail(activeThumbnail);

        if (activeThumbnail) {
          this.announceLiveRegion(
            activeMedia,
            activeThumbnail.dataset.mediaPosition
          );
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) =>
            element.removeAttribute('aria-current')
          );

        const button = thumbnail.querySelector('button');

        if (button) {
          button.setAttribute('aria-current', true);
        }

        if (
          this.elements.thumbnails.isSlideVisible(
            thumbnail,
            10
          )
        ) {
          return;
        }

        this.elements.thumbnails.slider.scrollTo({
          left: thumbnail.offsetLeft,
        });
      }

      announceLiveRegion(activeItem, position) {
        if (!this.elements.liveRegion) return;

        const image =
          activeItem.querySelector(
            '.product__modal-opener--image img'
          );

        if (!image) return;

        image.onload = () => {
          this.elements.liveRegion.setAttribute(
            'aria-hidden',
            false
          );

          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings.imageAvailable.replace(
              '[index]',
              position
            );

          setTimeout(() => {
            this.elements.liveRegion.setAttribute(
              'aria-hidden',
              true
            );
          }, 2000);
        };

        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();

        const deferredMedia =
          activeItem.querySelector('.deferred-media');

        if (deferredMedia) {
          deferredMedia.loadContent(false);
        }
      }

      preventStickyHeader() {
        this.stickyHeader =
          this.stickyHeader ||
          document.querySelector('sticky-header');

        if (!this.stickyHeader) return;

        this.stickyHeader.dispatchEvent(
          new Event('preventHeaderReveal')
        );
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;

        this.elements.viewer.slider.setAttribute(
          'role',
          'presentation'
        );

        this.elements.viewer.sliderItems.forEach(
          (slide) =>
            slide.setAttribute(
              'role',
              'presentation'
            )
        );
      }
    }
  );
}